import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import InstallPWA from "@/components/InstallPWA";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const authed = user && user !== true;
  const isAdmin = authed && user.role === "admin";

  const links = authed
    ? [
        { to: "/dashboard", label: t("nav.dashboard") },
        { to: "/course", label: t("nav.course") },
        { to: "/search", label: t("nav.search") },
        { to: "/bookmarks", label: t("nav.bookmarks") },
        { to: "/notes", label: t("nav.notes") },
        ...(isAdmin ? [{ to: "/admin", label: t("nav.admin") }] : []),
      ]
    : [];

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-void/90 backdrop-blur-md border-b border-ink/5" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to={authed ? "/dashboard" : "/"} className="flex items-baseline gap-2" data-testid="brand-logo">
          <span className="font-serif text-2xl text-ink tracking-tight">AI Lernwerk</span>
          <span className="w-1.5 h-1.5 bg-gold rounded-full inline-block" />
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm text-ink-soft">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.slice(1)}`}
              className={`transition-colors hover:text-ink ${loc.pathname === l.to ? "text-gold" : ""}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          <InstallPWA variant="desktop" />
          <LanguageSwitcher />
          {authed ? (
            <>
              <Link to="/profile" data-testid="nav-profile" className="text-sm text-ink-soft hover:text-ink transition-colors">
                {t("nav.profile")}
              </Link>
              <button onClick={() => { logout(); nav("/"); }} data-testid="nav-logout"
                className="text-sm text-ink-muted hover:text-gold transition-colors">
                {t("nav.logout")}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm text-ink-soft hover:text-ink transition-colors">
                {t("nav.login")}
              </Link>
              <Link to="/register" data-testid="nav-start"
                className="text-sm bg-gold text-void px-5 py-2 hover:bg-gold-hover transition-colors">
                {t("nav.start")}
              </Link>
            </>
          )}
        </div>

        <button className="md:hidden text-ink" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-graphite border-t border-ink/5 px-6 py-6 space-y-4" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block text-ink-soft hover:text-ink">
              {l.label}
            </Link>
          ))}
          <div className="pt-2"><LanguageSwitcher /></div>
          <div className="pt-1"><InstallPWA variant="mobile" /></div>
          {authed ? (
            <>
              <Link to="/profile" onClick={() => setOpen(false)} className="block text-ink-soft">{t("nav.profile")}</Link>
              <button onClick={() => { logout(); nav("/"); setOpen(false); }} className="block text-gold">{t("nav.logout")}</button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setOpen(false)} className="block text-ink-soft">{t("nav.login")}</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="block text-gold">{t("nav.start")}</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
